/** Supabase processLock / Web Locks contention — not an invalid reset link. */
import { supabase } from '../supabase';
import { hasValidRestaurantSelection } from './restaurantCheck';

export function isAuthLockError(error) {
  if (!error) return false;
  const message = typeof error === 'string' ? error : error.message || '';
  return Boolean(
    error?.isAcquireTimeout ||
    (typeof message === 'string' &&
      (message.includes('process lock') ||
        message.includes('Lock broken') ||
        message.includes('lock:sb-') ||
        (message.includes('lock') && message.includes('timed out')))) ||
    (error?.name === 'AbortError' &&
      typeof message === 'string' &&
      (message.includes('lock') || message.includes('timed out'))),
  );
}

export async function withAuthRetry(operation, { maxAttempts = 6, baseDelayMs = 350 } = {}) {
  let lastError;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const result = await operation();
      if (result?.error) {
        if (isAuthLockError(result.error) && attempt < maxAttempts - 1) {
          lastError = result.error;
          await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (attempt + 1)));
          continue;
        }
        return result;
      }
      return result;
    } catch (error) {
      lastError = error;
      if (!isAuthLockError(error) || attempt === maxAttempts - 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (attempt + 1)));
    }
  }
  throw lastError || new Error('Auth operation timed out');
}

export async function withTimeout(promise, ms, message = 'Request timed out') {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export function formatAuthLockMessage() {
  return 'Sign-in is busy. Close other Table Tap tabs, wait a moment, and try again.';
}

export function formatPasswordResetError(error) {
  if (isAuthLockError(error)) {
    return {
      title: 'Please try again',
      text: 'We could not verify your reset link because another Table Tap tab is busy. Close other tabs, refresh this page, or open the email link again.',
    };
  }

  const message = error?.message || String(error || '');
  if (/expired|invalid|not found|already been used/i.test(message)) {
    return {
      title: 'Reset link expired',
      text: 'This password reset link is no longer valid. Request a new one below.',
    };
  }

  return {
    title: 'Could not verify reset link',
    text: message || 'Something went wrong verifying your link. Please request a new reset email.',
  };
}

/** Full-page navigation after auth — avoids React Router stalls from auth lock contention. */
export function hardAuthRedirect(path) {
  if (typeof window !== 'undefined' && path) {
    window.location.assign(path);
  }
}

export function hasRecoveryParams(location) {
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const queryParams = new URLSearchParams(location?.search || '');

  return Boolean(
    (hashParams.get('access_token') && hashParams.get('type') === 'recovery') ||
    queryParams.get('code') ||
    (queryParams.get('token_hash') && queryParams.get('type') === 'recovery'),
  );
}

/** Clear Supabase auth keys when signOut cannot acquire the auth lock. */
export function clearSupabaseAuthStorage() {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(localStorage)
      .filter((k) => k.toLowerCase().includes('sb-') || k.includes('supabase'))
      .forEach((k) => localStorage.removeItem(k));

    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name.includes('sb-') || name.includes('supabase')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      }
    });
  } catch {
    /* ignore storage errors */
  }
}

/** Auth API call with retry + wall-clock timeout (prevents infinite "Loading…" spinners). */
export async function runAuthOperation(
  operation,
  { maxAttempts = 4, timeoutMs = 15000, timeoutMessage = 'Request timed out' } = {},
) {
  return withTimeout(withAuthRetry(operation, { maxAttempts }), timeoutMs, timeoutMessage);
}

/**
 * Sign out without blocking the UI. Clears local session if GoTrue lock times out.
 * @returns {Promise<{success: boolean}>}
 */
export async function signOutSafely() {
  try {
    const { error } = await runAuthOperation(() => supabase.auth.signOut(), {
      maxAttempts: 2,
      timeoutMs: 8000,
      timeoutMessage: 'Sign out timed out',
    });
    if (error && !isAuthLockError(error)) throw error;
  } catch (error) {
    if (!isAuthLockError(error)) {
      clearSupabaseAuthStorage();
      throw error;
    }
    clearSupabaseAuthStorage();
  }
  return { success: true };
}

/** Sign out then full-page redirect — use for all logout buttons. */
export async function signOutAndRedirect(loginPath = '/login') {
  try {
    await signOutSafely();
  } catch {
    clearSupabaseAuthStorage();
  }
  hardAuthRedirect(loginPath);
}

/** Logged-in customer landing — hard redirect avoids router stalls after auth. */
export function redirectLoggedInCustomer(search = '') {
  const dest = hasValidRestaurantSelection() ? '/home' : '/restaurant-selection';
  hardAuthRedirect(`${dest}${search || ''}`);
}
